import React, { useMemo } from 'react'
import { message, Select } from 'antd'
import isNil from 'lodash/isNil'
import { extractApiErrorMessage } from 'helpers/api-error-message'
import {
  InvoiceDeliveryItemDto,
  InvoiceTemplate,
} from '../../../api/dto/invoice-delivery.dto'
import { useUpdateInvoiceDeliveryTemplateMutation } from '../../../api/invoice-delivery-api-client'
import { hasBeenSent } from '../utils'
import { EmailTemplateTag } from './EmailTemplateTag'
import {
  INVOICE_TEMPLATE_CUSTOMER_LABELS,
  getAllowedTemplatesForPeriod,
  INVOICE_TEMPLATE_LABELS,
  resolveTemplatePaymentType,
} from './invoiceTemplates'
import { TemplateEditorHint } from './EmailPreview.styles'
import styles from './TemplatePicker.module.scss'

interface TemplateEditorProps {
  delivery: InvoiceDeliveryItemDto
  /** The month's invoices, to grey out templates the company already has. */
  monthDeliveries: InvoiceDeliveryItemDto[]
  onUpdated: (delivery: InvoiceDeliveryItemDto) => void
}

/**
 * The invoice's email template, changeable until the invoice is emailed. Each
 * company has at most one invoice per template a month, so templates it
 * already has are disabled.
 */
const TemplateEditor: React.FC<TemplateEditorProps> = ({
  delivery,
  monthDeliveries,
  onUpdated,
}) => {
  const mutation = useUpdateInvoiceDeliveryTemplateMutation({
    onSuccess: (updated) => {
      message.success(
        `Template changed to ${INVOICE_TEMPLATE_LABELS[updated.invoiceTemplate]}`
      )
      onUpdated({
        ...delivery,
        ...updated,
        paymentType: resolveTemplatePaymentType(
          updated.invoiceTemplate,
          delivery.profilePaymentType
        ),
      })
    },
    onError: (err) => {
      message.error(extractApiErrorMessage(err, 'Failed to change template'))
    },
  })

  const options = useMemo(() => {
    const taken = new Set(
      isNil(delivery.clientId)
        ? []
        : monthDeliveries
            .filter(
              (d) =>
                d.clientId === delivery.clientId &&
                d.invoiceTemplate !== delivery.invoiceTemplate
            )
            .map((d) => d.invoiceTemplate)
    )
    const allowed = getAllowedTemplatesForPeriod(delivery.billingPeriodStart)
    const templates = allowed.includes(delivery.invoiceTemplate)
      ? allowed
      : [delivery.invoiceTemplate, ...allowed]
    return templates.map((template) => ({
      value: template,
      disabled: taken.has(template),
      title: INVOICE_TEMPLATE_CUSTOMER_LABELS[template],
      label: taken.has(template)
        ? `${INVOICE_TEMPLATE_LABELS[template]} · already uploaded`
        : INVOICE_TEMPLATE_LABELS[template],
    }))
  }, [
    delivery.billingPeriodStart,
    delivery.clientId,
    delivery.invoiceTemplate,
    monthDeliveries,
  ])

  const isHistorical = !delivery.isLatest
  if (isHistorical || hasBeenSent(delivery)) {
    return (
      <TemplateEditorHint>
        <EmailTemplateTag
          template={delivery.invoiceTemplate}
          paymentType={delivery.paymentType}
        />
        {isHistorical ? 'Only the latest version can change template.' : null}
      </TemplateEditorHint>
    )
  }

  const handleChange = (invoiceTemplate: InvoiceTemplate) => {
    if (invoiceTemplate === delivery.invoiceTemplate) return
    mutation.mutate({ id: delivery.id, body: { invoiceTemplate } })
  }

  return (
    <Select
      size="small"
      value={delivery.invoiceTemplate}
      options={options}
      onChange={handleChange}
      loading={mutation.isPending}
      disabled={mutation.isPending}
      popupMatchSelectWidth={false}
      aria-label="Email template"
      className={styles.templateSelect}
    />
  )
}

export { TemplateEditor }
